import { inViewFadeUp } from '@helpers/animate';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { contentStyles, h3, link, paragraph } from '@helpers/typography';
import SectionWrapper from '@stories/Components/SectionWrapper/SectionWrapper';
import { mix } from 'polished';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  position: relative;
  background: var(--bgColor);
  color: var(--fgColor);
  overflow: hidden;

  &[data-bg='white'] {
    --bgColor: ${brand.white};
    --fgColor: ${brand.black};
    margin: var(--sectionSpacing) 0;
  }

  &:not([data-bg='white']) {
    padding: var(--sectionSpacing) 0;
  }

  &[data-bg='lightGrey'] {
    --bgColor: ${mix(0.25, brand.grey.otherGrey, brand.white)};
    --fgColor: ${brand.black};
  }

  &[data-bg='grey'] {
    --bgColor: ${brand.grey.grey3};
    --fgColor: ${brand.black};
  }

  &[data-bg='black'] {
    --bgColor: ${brand.grey.grey6};
    --fgColor: ${brand.white};
  }
`;
const Inner = styled.div `
  ${baseGrid};
  align-items: flex-start;
  grid-template-rows: auto 1fr;
  width: 100%;
`;
const ContentWrapper = styled.div `
  grid-column: 1 / span 12;
  grid-row: 1;

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 5;
    grid-row: 1;
  }

  @media ${from(Device.DesktopSmall)} {
    grid-column: 2 / span 4;
  }
`;
const Title = styled.h2 `
  ${h3};
  margin-bottom: 28px;

  @media ${from(Device.TabletLarge)} {
    margin-bottom: 58px;
  }

  ${inViewFadeUp({ delay: 0, duration: 0.3 })};
`;
const Content = styled.div `
  ${contentStyles};
  margin-bottom: 64px;

  @media ${from(Device.TabletLarge)} {
    margin-bottom: 58px;
  }

  [data-single-list='true'] & {
    margin-bottom: 48px;

    @media ${from(Device.TabletLarge)} {
      margin-bottom: 48px;
    }
  }

  ${inViewFadeUp({ delay: 0.15, duration: 0.3 })};
`;
const CtaWrapper = styled.div `
  grid-column: 1 / span 12;
  grid-row: 3;

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 5;
    grid-row: 2;
  }

  @media ${from(Device.DesktopSmall)} {
    grid-column: 2 / span 4;
  }

  [data-single-list='true'] & {
    margin-top: 48px;
  }

  ${inViewFadeUp({ delay: 0.3, duration: 0.3 })};
`;
const ListsWrapper = styled.div `
  grid-column: 1 / span 12;
  grid-row: 2;

  @media ${until(Device.Tablet)} {
    display: flex;
    flex-direction: column;
  }

  @media ${from(Device.Tablet)} {
    display: flex;
    flex-direction: row;
    margin-left: calc(var(--gutterWidth) / 2 * -1);
    margin-right: calc(var(--gutterWidth) / 2 * -1);
  }

  @media ${until(Device.TabletLarge)} {
    margin-bottom: 32px;
  }

  @media ${from(Device.TabletLarge)} {
    align-self: center;
    grid-column: -8 / span 7;
    grid-row: 1 / span 2;
    margin-bottom: 0;
  }

  @media ${from(Device.DesktopSmall)} {
    grid-column: -7 / span 6;
  }

  [data-single-list='true'] & {
    border-top: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    max-width: 492px;
    padding-top: 28px;
    padding-bottom: 28px;
    margin: 0;

    @media ${from(Device.TabletLarge)} {
      grid-column: 7 / span 5;
      padding-top: 64px;
      padding-bottom: 64px;
    }

    @media ${from(Device.DesktopSmall)} {
      grid-column: 8 / span 4;
    }
  }

  ${inViewFadeUp({ delay: 0.15, duration: 0.3 })};
`;
const ListWrapper = styled.div `
  @media ${until(Device.Tablet)} {
    &:not(:last-child) {
      margin-bottom: 42px;
    }
  }

  @media ${from(Device.Tablet)} {
    flex: 1 1 50%;
    padding: 0 calc(var(--gutterWidth) / 2);
  }

  [data-single-list='true'] & {
    padding: 0;
  }
`;
const ListTitle = styled.h3 `
  position: relative;
  ${fonts.helvetica.regular};
  font-size: 14px;
  letter-spacing: 0.1em;
  line-height: 26px;
  margin: 0 0 26px;
  padding: 0 0 8px;
  text-transform: uppercase;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    display: block;
    background: var(--fgColor);
    height: 2px;
    width: 100%;
  }

  @media ${from(Device.TabletLarge)} {
    margin: 0 0 32px;
    padding: 0 0 24px;

    &::after {
      height: 4px;
    }
  }
`;
const List = styled.ul `
  ${paragraph};

  display: block;
  list-style-type: disc;
  list-style-type: '-';
  margin-bottom: 0;
  padding-left: 1ch;
`;
const Item = styled.li `
  padding-left: 1.2em;

  &:not(:last-child) {
    margin-bottom: 16px;
  }

  [data-single-list='true'] & {
    padding-left: calc(64px - 1ch);
  }
`;
const Link = styled.a `
  color: currentColor;

  a& {
    ${link};
  }
`;
const TextListSectionStyles = {
    Container,
    Content,
    ContentWrapper,
    CtaWrapper,
    Inner,
    Item,
    Link,
    List,
    ListTitle,
    ListWrapper,
    ListsWrapper,
    Title,
};
export default TextListSectionStyles;
